import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, ArrowRight, ArrowLeft, CreditCard, Sparkles, Loader2, Mail, Clock, Users, Layers, Check } from "lucide-react";
import { toast } from "sonner";
import TileSphereLogo from "@/components/TileSphereLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StepBadge({ n, label, active, done }) {
  return (
    <div className={`flex items-center gap-2 ${active ? "" : "opacity-50"}`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold ${done ? "bg-emerald-500 text-white" : active ? "bg-orange-500 text-white" : "bg-white/10 text-white/60"}`}>
        {done ? <CheckCircle2 className="w-4 h-4" /> : n}
      </div>
      <span className="text-xs font-semibold uppercase tracking-widest hidden sm:block">{label}</span>
    </div>
  );
}

function fmtDateLong(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso; }
}

export default function RegisterPage() {
  const [params] = useSearchParams();
  const portal = (params.get("portal") || "ORGANIZATION").toUpperCase();
  const planCode = (params.get("plan") || "").toUpperCase();
  const nav = useNavigate();

  const [plan, setPlan] = useState(null);
  // Steps: 0=trial preview (only if trial), 1=form, 2=payment, 3=done
  const [step, setStep] = useState(0);
  const [details, setDetails] = useState({ company_name: "", contact_name: "", email: "", phone: "", address: "", gstin: "", message: "" });

  const [regId, setRegId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!planCode) { nav(`/subscribe?portal=${portal}`); return; }
    axios.get(`${API}/platform/public/plans`).then(({ data }) => {
      const p = (data || []).find((x) => x.code === planCode);
      if (!p) { toast.error("Plan not found"); return; }
      setPlan(p);
      // If plan is NOT a trial, skip the preview step entirely
      setStep(p.is_trial ? 0 : 1);
    });
  }, [planCode, portal, nav]);

  const isTrial = !!plan?.is_trial;

  // Super-Admin-defined trial config — read-only for the user
  const trialSummary = useMemo(() => {
    if (!plan?.is_trial) return null;
    const tc = plan.trial_config || {};
    const mode = tc.mode || "days";
    if (mode === "date_range" && tc.start_date && tc.end_date) {
      const start = new Date(tc.start_date);
      const end = new Date(tc.end_date);
      const days = Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)));
      return { mode: "date_range", days, start: tc.start_date, end: tc.end_date };
    }
    const days = tc.days || plan.trial_days || 14;
    return { mode: "days", days };
  }, [plan]);

  const canSubmit = details.company_name && details.contact_name && details.email;

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        portal, plan_code: planCode,
        billing_cycle: isTrial ? "TRIAL" : "MONTHLY",
        ...details,
      };
      // For trials: NEVER send trial_mode / trial_days / trial_start / trial_end from the client.
      // The backend now falls back to plan.trial_config for the definitive Super-Admin-defined duration.
      const { data } = await axios.post(`${API}/public/register/init`, body);
      setRegId(data.registration_id);
      if (data.is_trial) {
        setDone({ status: data.status, is_trial: true, trial_days: data.trial_days, trial_end: data.trial_end });
        setStep(3);
      } else {
        setStep(2);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  const payAndVerify = async () => {
    setBusy(true);
    try {
      const orderResp = await axios.post(`${API}/public/register/payment/create-order`, { registration_id: regId });
      const provider = orderResp.data.provider;
      if (provider === "razorpay") {
        await new Promise((resolve) => {
          const s = document.createElement("script");
          s.src = "https://checkout.razorpay.com/v1/checkout.js";
          s.onload = resolve; document.body.appendChild(s);
        });
        // eslint-disable-next-line no-undef
        const rzp = new window.Razorpay({
          key: orderResp.data.key_id,
          amount: orderResp.data.amount,
          currency: orderResp.data.currency,
          order_id: orderResp.data.order_id,
          name: "TileSphere",
          description: `${plan?.name} — ${details.company_name}`,
          prefill: { name: details.contact_name, email: details.email, contact: details.phone },
          handler: async (res) => {
            try {
              await axios.post(`${API}/public/register/payment/verify`, {
                registration_id: regId,
                razorpay_payment_id: res.razorpay_payment_id,
                razorpay_order_id: res.razorpay_order_id,
                razorpay_signature: res.razorpay_signature,
              });
              setDone({ status: "PENDING_APPROVAL", is_trial: false, paid: true });
              setStep(3);
            } catch (e) { toast.error(e.response?.data?.detail || "Payment verification failed"); }
          },
        });
        rzp.open();
      } else {
        await axios.post(`${API}/public/register/payment/verify`, { registration_id: regId });
        setDone({ status: "PENDING_APPROVAL", is_trial: false, paid: true, mock: true });
        setStep(3);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment failed");
    } finally { setBusy(false); }
  };

  const heroTitle = useMemo(() => {
    if (!plan) return "Register";
    if (plan.is_trial) return "Start your free trial";
    return `Subscribe to ${plan.name}`;
  }, [plan]);

  return (
    <div className="min-h-screen bg-[#0a0a14] text-white flex flex-col" data-testid="register-page">
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/40">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/"><TileSphereLogo /></Link>
          <div className="flex items-center gap-4">
            {isTrial && <StepBadge n={1} label="Trial" active={step === 0} done={step > 0} />}
            <StepBadge n={isTrial ? 2 : 1} label="Details" active={step === 1} done={step > 1} />
            {!isTrial && <StepBadge n={2} label="Payment" active={step === 2} done={step > 2} />}
            <StepBadge n={isTrial ? 3 : 3} label="Done" active={step === 3} done={false} />
          </div>
        </div>
      </header>

      <main className="flex-1 py-10 sm:py-14 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/15 text-[11px] font-bold uppercase tracking-[0.25em] text-orange-300">
              <Sparkles className="w-3 h-3" /> {portal.charAt(0)}{portal.slice(1).toLowerCase()} portal
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-black mt-3">{heroTitle}</h1>
            {plan && step !== 0 && (
              <p className="mt-3 text-white/60">
                {plan.is_trial ? "You're one step away from your TileSphere trial." : `₹${Number(plan.monthly_price || plan.base_price_monthly || 0).toLocaleString()}/mo · billed monthly`}
              </p>
            )}
          </motion.div>

          <div className="mt-10 rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-6 sm:p-8">
            {/* STEP 0 — TRIAL PREVIEW (Super Admin defined, read-only) */}
            {step === 0 && plan && isTrial && trialSummary && (
              <div className="space-y-6" data-testid="reg-step-trial-preview">
                <div className="text-center">
                  <Badge className="bg-orange-400/20 text-orange-200 border-orange-400/40 px-3 py-1 text-xs">Free trial · configured by TileSphere</Badge>
                  <div className="font-display text-3xl sm:text-4xl font-black mt-4">{plan.name}</div>
                  {plan.tagline && <p className="text-white/60 mt-2">{plan.tagline}</p>}
                </div>

                <div className="rounded-2xl bg-gradient-to-br from-orange-500/[0.08] to-pink-500/[0.04] border border-orange-400/30 p-6" data-testid="trial-preview-card">
                  <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-orange-300 mb-3">Your trial includes</div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="rounded-xl bg-black/30 border border-white/10 p-4 text-center">
                      <Clock className="w-6 h-6 mx-auto text-orange-300" />
                      <div className="font-display text-3xl font-black mt-2" data-testid="trial-preview-days">{trialSummary.days}</div>
                      <div className="text-[10px] uppercase tracking-widest text-white/50 mt-1">days free</div>
                      {trialSummary.mode === "date_range" && (
                        <div className="text-[10px] text-white/60 mt-2">
                          {fmtDateLong(trialSummary.start)} → {fmtDateLong(trialSummary.end)}
                        </div>
                      )}
                    </div>
                    <div className="rounded-xl bg-black/30 border border-white/10 p-4 text-center">
                      <Users className="w-6 h-6 mx-auto text-emerald-300" />
                      <div className="font-display text-3xl font-black mt-2" data-testid="trial-preview-users">{plan.max_users_default || plan.trial_max_users || "∞"}</div>
                      <div className="text-[10px] uppercase tracking-widest text-white/50 mt-1">users</div>
                    </div>
                    <div className="rounded-xl bg-black/30 border border-white/10 p-4 text-center">
                      <Layers className="w-6 h-6 mx-auto text-blue-300" />
                      <div className="font-display text-3xl font-black mt-2" data-testid="trial-preview-processes">{(plan.features || []).length}</div>
                      <div className="text-[10px] uppercase tracking-widest text-white/50 mt-1">processes</div>
                    </div>
                  </div>

                  {plan.features && plan.features.length > 0 && (
                    <div className="mt-5 pt-5 border-t border-white/10">
                      <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-orange-300 mb-3">What's included</div>
                      <ul className="grid sm:grid-cols-2 gap-2">
                        {plan.features.map((f, idx) => (
                          <li key={idx} className="text-sm text-white/80 flex items-start gap-2">
                            <Check className="w-4 h-4 mt-0.5 text-emerald-400 flex-shrink-0" /> <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <p className="text-xs text-white/50 text-center max-w-md mx-auto">
                  Trial length is configured by TileSphere's team. When your {trialSummary.days}-day trial ends, you can upgrade to a paid plan or your account will be paused.
                </p>

                <div className="flex justify-between">
                  <Button variant="ghost" onClick={() => nav(`/subscribe?portal=${portal}`)} className="text-white/60 hover:text-white"><ArrowLeft className="w-4 h-4 mr-1" /> Change plan</Button>
                  <Button onClick={() => setStep(1)} data-testid="trial-preview-continue" className="bg-gradient-to-r from-orange-500 to-pink-500 h-11 font-bold px-6">
                    Continue to registration <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 1 — CONTACT / DETAILS FORM */}
            {step === 1 && (
              <div className="space-y-4" data-testid="reg-step-1">
                {isTrial && trialSummary && (
                  <div className="rounded-xl bg-orange-500/10 border border-orange-400/30 p-3 flex items-center gap-3" data-testid="reg-trial-badge">
                    <Sparkles className="w-4 h-4 text-orange-300 flex-shrink-0" />
                    <div className="text-sm">
                      Applying for a <b className="text-orange-200">{trialSummary.days}-day free trial</b> of <b>{plan.name}</b>
                      {trialSummary.mode === "date_range" && (
                        <span className="text-white/60"> ({fmtDateLong(trialSummary.start)} → {fmtDateLong(trialSummary.end)})</span>
                      )}
                    </div>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2 sm:col-span-2"><Label>Company / Organization name</Label>
                    <Input value={details.company_name} onChange={(e) => setDetails({ ...details, company_name: e.target.value })} data-testid="reg-company" className="bg-white/5 border-white/10 text-white" /></div>
                  <div className="space-y-2"><Label>Your name</Label>
                    <Input value={details.contact_name} onChange={(e) => setDetails({ ...details, contact_name: e.target.value })} data-testid="reg-name" className="bg-white/5 border-white/10 text-white" /></div>
                  <div className="space-y-2"><Label>Work email</Label>
                    <Input type="email" value={details.email} onChange={(e) => setDetails({ ...details, email: e.target.value })} data-testid="reg-email" className="bg-white/5 border-white/10 text-white" /></div>
                  <div className="space-y-2"><Label>Phone</Label>
                    <Input value={details.phone} onChange={(e) => setDetails({ ...details, phone: e.target.value })} data-testid="reg-phone" className="bg-white/5 border-white/10 text-white" /></div>
                  <div className="space-y-2"><Label>GSTIN <span className="text-white/40">(optional)</span></Label>
                    <Input value={details.gstin} onChange={(e) => setDetails({ ...details, gstin: e.target.value })} data-testid="reg-gstin" className="bg-white/5 border-white/10 text-white" /></div>
                  <div className="space-y-2 sm:col-span-2"><Label>Address <span className="text-white/40">(optional)</span></Label>
                    <Input value={details.address} onChange={(e) => setDetails({ ...details, address: e.target.value })} className="bg-white/5 border-white/10 text-white" /></div>
                  <div className="space-y-2 sm:col-span-2"><Label>Anything else <span className="text-white/40">(optional)</span></Label>
                    <Textarea value={details.message} onChange={(e) => setDetails({ ...details, message: e.target.value })} className="bg-white/5 border-white/10 text-white" rows={2} /></div>
                </div>

                <div className="flex justify-between mt-6">
                  <Button variant="ghost" onClick={() => (isTrial ? setStep(0) : nav(`/subscribe?portal=${portal}`))} className="text-white/60 hover:text-white"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
                  <Button onClick={submit} disabled={!canSubmit || busy} data-testid="reg-submit" className="bg-gradient-to-r from-orange-500 to-pink-500 h-11 font-bold">
                    {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    {isTrial ? "Request free trial" : "Continue to payment"} <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 2 — PAID PAYMENT */}
            {step === 2 && (
              <div className="space-y-6 text-center py-8" data-testid="reg-step-2">
                <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center">
                  <CreditCard className="w-8 h-8 text-white" />
                </div>
                <div>
                  <div className="font-display text-2xl font-black">Complete payment</div>
                  <div className="text-white/60 mt-1">You'll be redirected to Razorpay checkout. Once payment is confirmed, our Super Admin team reviews and approves within 24 hrs.</div>
                </div>
                <Button onClick={payAndVerify} disabled={busy} data-testid="pay-btn" className="bg-gradient-to-r from-orange-500 to-pink-500 h-12 px-8 font-bold">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                  Pay now
                </Button>
              </div>
            )}

            {/* STEP 3 — DONE */}
            {step === 3 && done && (
              <div className="space-y-4 text-center py-6" data-testid="reg-step-done">
                <div className="mx-auto w-16 h-16 rounded-2xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                </div>
                <div>
                  <div className="font-display text-3xl font-black">
                    {done.is_trial ? "Trial request submitted!" : "Payment confirmed!"}
                  </div>
                  <div className="text-white/70 mt-2">
                    {done.is_trial
                      ? <>Your <b>{done.trial_days}-day free trial</b> request for <b>{plan?.name}</b> was sent to our Super Admin team.</>
                      : <>Payment for <b>{plan?.name}</b> is confirmed. Our Super Admin will approve your account shortly.</>}
                  </div>
                  {done.mock && <div className="mt-2 text-[11px] text-orange-300">(MOCK payment — real Razorpay activates when Super Admin pastes API keys in the console.)</div>}
                </div>
                <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-5 text-left max-w-md mx-auto">
                  <div className="flex items-center gap-2 text-sm font-semibold"><Mail className="w-4 h-4 text-orange-300" /> Next step</div>
                  <div className="text-sm text-white/70 mt-2">Once approved, you'll receive an email at <b>{details.email}</b> with your custom portal URL and login credentials.</div>
                </div>
                <Link to="/" className="inline-block mt-4">
                  <Button variant="outline" className="border-white/20 hover:bg-white/10">Back to home</Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
